import { z } from 'zod';

/*
 * Runtime contract for the `carrefour-br · world-cup-engagement` document
 * payload. Mirrors db/reports/carrefour-br-world-cup.md in the client-portal
 * backend repo; if that contract changes, update this schema and the fixture.
 */

/*
 * A KPI metric carries both a machine value and a pre-formatted display string.
 * Optional annotations (label, currency, unit, foot) are accepted but not required.
 * The pipeline freely relabels metrics between Money / Rate / Count shapes (e.g.
 * `segments[].avg_basket` started as a Money and became a Rate of "items / txn") —
 * the UI renders `display` and doesn't care which axis it's on. Stricter contracts
 * can be re-introduced once the data shape stabilizes.
 */
const metricSchema = z.object({
  value: z.number(),
  display: z.string(),
  label: z.string().optional(),
  currency: z.string().optional(),
  unit: z.string().optional(),
  foot: z.string().optional(),
});

// Kept as named aliases so call sites read as intent ("this is meant to be money") —
// today they all share the same permissive shape.
const moneySchema = metricSchema;
const countSchema = metricSchema;
const rateSchema = metricSchema;

const weeklySeriesSchema = z.object({
  label: z.string(),
  unit: z.string().optional(),
  series: z.array(z.object({ iso_week: z.number().int(), value: z.number() })),
});

// France-only: consents-per-day is a date series (one bucket per calendar
// day in the campaign window) rather than the weekly-ISO bucket layout used
// elsewhere. Kept separate so the WeeklyBars component stays simple — a
// dedicated DailyBars renderer takes this shape.
const dailySeriesSchema = z.object({
  label: z.string(),
  unit: z.string().optional(),
  series: z.array(z.object({ date: z.string(), value: z.number() })),
});

const brandSchema = z.object({
  id: z.string(),
  name: z.string(),
  pack: z.string(), // 'Stadium' | 'Kiosks' (Brazil) | 'Gold' | 'Silver' | 'Carrefour' (Spain) — kept open so new regions don't fight the schema
  weeks: z.string(),
  game: z.string().nullable(),
  color: z.string(),
});

const campaignSchema = z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
  currency: z.string(),
  locale: z.string(),
  date_range: z.object({ start: z.string(), end: z.string(), weeks: z.number().int() }),
  weeks: z.array(z.object({ iso_week: z.number().int(), label: z.string(), start: z.string(), end: z.string() })),
  brands: z.array(brandSchema),
});

const overviewSegmentSchema = z.object({
  brand_id: z.string(),
  brand_name: z.string(),
  pack: z.string(), // 'Stadium' | 'Kiosks' (Brazil) | 'Gold' | 'Silver' | 'Carrefour' (Spain) — kept open so new regions don't fight the schema
  weeks: z.string(),
  game: z.string().nullable(),
  // Per-region: Spain ships no sales data, so revenue / frequency / avg_basket
  // may be absent. Keep optional and let the UI hide-flags handle rendering.
  // `users` is also optional now — engagement-only markets (France) don't
  // have a "distinct users per brand" source and instead populate `plays`.
  revenue: moneySchema.optional(),
  users: countSchema.optional(),
  frequency: rateSchema.optional(),
  avg_basket: moneySchema.optional(),
  // Per-brand games played + missions — sourced from
  // games_missions.games_by_brand and merged into segments at the UI
  // boundary. Shown by SegmentTable in engagement-only markets in place of
  // the sales columns.
  plays: countSchema.optional(),
  missions: countSchema.optional(),
});

const overviewSectionSchema = z.object({
  business_kpis: z.object({
    // Sales-data KPIs are optional — Spain omits them entirely.
    revenue: moneySchema.optional(),
    users_traffic: countSchema,
    purchase_frequency: rateSchema.optional(),
    avg_basket: moneySchema.optional(),
    // France-only — headline "Consents Given" KPI. The full breakdown
    // lives in sections.consents (separate sub-tab).
    consents_total: countSchema.optional(),
  }),
  engagement_kpis: z.object({
    unique_optins: countSchema,
    unique_visitors: countSchema,
    games_played: countSchema,
    missions_completed: countSchema,
    rewards_awarded: countSchema,
    // Mean of the DAILY distinct-visitor counts (not visitors ÷ days). Optional
    // — only campaigns whose design asks for it ship it (Coca-Cola Quick).
    avg_daily_visitors: countSchema.optional(),
    // Rewards actually redeemed by the user, as opposed to won. Optional —
    // needs a redemption signal in the source data.
    rewards_claimed: countSchema.optional(),
  }),
  // Backend may omit `segments` when a region has no per-brand metrics; the
  // UI synthesises rows from `campaign.brands` in that case so the brand list
  // still appears.
  segments: z.array(overviewSegmentSchema).optional(),
});

const funnelStepSchema = z.object({
  step: z.string(),
  label: z.string(),
  value: z.number(),
  pct: z.number(),
  drop_pct_from_prev: z.number().nullable(),
});

// Engagement section accepts EITHER weekly (BR contract) OR daily (Spain/France
// contract) series. Region pipelines moved to daily resolution after BR
// shipped; rather than force-migrate the BR calcs, the UI flexes. Both pairs
// are optional independently — views pick `daily_*` if present, else fall back
// to `weekly_*`, else render an empty chart placeholder.
const engagementSectionSchema = z.object({
  kpis: z.object({
    unique_optins: countSchema,
    unique_visitors: countSchema,
    // Distinct players (operations), optional — Spain ships it.
    unique_players: countSchema.optional(),
  }),
  weekly_unique_visitors: weeklySeriesSchema.optional(),
  weekly_games_played: weeklySeriesSchema.optional(),
  daily_unique_visitors: dailySeriesSchema.optional(),
  daily_games_played: dailySeriesSchema.optional(),
  funnel: z.array(funnelStepSchema),
  // Time-in-app metrics from the loyalty-analytics events rollup. Optional —
  // only the markets running the events analytics (ES + FR) ship it; the
  // Engagement view renders the block only when present.
  // `total` = time across all screens (incl. the hub); `avg_per_session` = mean
  // session duration; `by_brand` = per-brand dwell (busiest first), each row's
  // `avg` being the mean time per visit to that brand (time ÷ visits). Every
  // field is optional so older / partial payloads still parse — see the Session
  // & Time-Spent methodology.
  time_in_app: z
    .object({
      total: metricSchema.optional(),
      avg_per_visitor: metricSchema.optional(),
      avg_per_session: metricSchema.optional(),
      by_brand: z.array(z.object({ brand_id: z.string(), name: z.string(), time: metricSchema, avg: metricSchema.optional() })).optional(),
      // ISO date (YYYY-MM-DD) the market started collecting events — drives the
      // "tracked since" note. Optional; the view falls back to a default date.
      tracked_since: z.string().optional(),
    })
    .optional(),
});

const gamesByBrandSchema = z.object({
  brand_id: z.string(),
  brand: z.string(),
  // Nullable: Spain ships per-brand rows without mapping a game to each brand.
  game: z.string().nullable(),
  plays: countSchema,
  // Per-brand missions (SUM(balance) over the brand's MC). France-only today;
  // BR/Spain ship missions only at the KPI level, not per brand. Optional.
  missions: countSchema.optional(),
  avg_per_user: z.number(),
  completion_pct: z.number(),
});

const gameSchema = z.object({
  id: z.string(),
  name: z.string(),
  brand_ids: z.array(z.string()),
  plays: countSchema,
  avg_per_user: z.number(),
  completion_pct: z.number(),
});

// A per-day, per-brand stacked daily series. `brands` are the stack keys
// (sorted by total desc); each day's `counts` maps brand_id → value (only
// non-zero brands present). Reused by:
//   - games_missions.players_by_day  (distinct players per day per brand)
//   - rewards_coupons.coupons_by_day (coupons distributed per day per brand)
const brandDailyStackSchema = z.object({
  label: z.string(),
  unit: z.string().optional(),
  brands: z.array(z.object({ brand_id: z.string(), name: z.string() })),
  series: z.array(z.object({ date: z.string(), counts: z.record(z.string(), z.number()) })),
});

const gamesMissionsSectionSchema = z.object({
  kpis: z.object({
    total_games: countSchema,
    avg_games_per_user: countSchema,
    missions_done: countSchema,
  }),
  // Same weekly-vs-daily flex as engagementSection — see comment there.
  weekly_games_played: weeklySeriesSchema.optional(),
  weekly_missions: weeklySeriesSchema.optional(),
  daily_games_played: dailySeriesSchema.optional(),
  daily_missions: dailySeriesSchema.optional(),
  games_by_brand: z.array(gamesByBrandSchema),
  games: z.array(gameSchema),
  // Spain-only: distinct players per day per brand (stacked). Optional.
  players_by_day: brandDailyStackSchema.optional(),
  // How plays were funded — free daily allowance vs earned by spending (from
  // the game docs' `assignReason`). France 50/3 ships it; optional elsewhere.
  // `slices` may carry extra entries beyond free/purchased when the platform
  // reports a funding source we don't classify yet — render them as-is rather
  // than assuming two.
  games_by_source: z
    .object({
      label: z.string(),
      total: z.number(),
      slices: z.array(z.object({ key: z.string(), name: z.string(), value: z.number(), pct: z.number() })),
    })
    .optional(),
  // Same split per day, in the shared stacked-daily shape (its `brands` field
  // carries the funding sources) — turns the games bar chart into a stack.
  games_by_source_day: brandDailyStackSchema.optional(),
});

// `name` is nullable: France ships `null` when the prize's category doesn't
// map to a known sponsor brand (rare but happens when a benefit's category
// is missing or points at a non-brand value like 'CREDITED').
const topPrizeSchema = z.object({
  name: z.string().nullable(),
  brand_id: z.string().nullable(),
  won: z.number(),
});

// Per-brand coupon usage breakdown. Backend ships one row per brand, sorted
// by total volume desc. All three counters are optional only at the schema
// boundary — real rows always carry all three; making them optional shields
// the UI from a partial-write during the calc's roll-out window.
const couponByBrandSchema = z.object({
  brand_id: z.string(),
  name: z.string(),
  // 'credit' rewards (e.g. Carrefour ChequeAhorro) have no email/in-store/
  // not-used channel split — the UI shows them as a single total bar. Defaults
  // to 'coupon' when the backend omits it.
  kind: z.enum(['coupon', 'credit']).optional(),
  used_email: countSchema.optional(),
  used_in_store: countSchema.optional(),
  not_used: countSchema.optional(),
  // Total rewards for the brand (all channels). Authoritative headline number;
  // for coupons it equals email+in-store+not-used, for credit it's the only one.
  total: countSchema.optional(),
});

// Per-brand remaining coupon inventory ("how many coupons left"). Each row sums
// a brand's ACTIVE coupon benefits: `left` = barcodes still AVAILABLE, `issued`
// = handed out, `total` = pool size. Optional — only regions that pre-load
// barcode inventory (Brazil today) ship it; the view hides the panel otherwise.
const couponStockSchema = z.object({
  brand_id: z.string(),
  name: z.string(),
  // How many active benefits rolled into this brand's row (diagnostic).
  benefits: z.number().optional(),
  left: countSchema,
  issued: countSchema,
  total: countSchema,
});

const rewardsCouponsSectionSchema = z.object({
  // Section heading + eyebrow overrides. The reward noun is campaign-specific
  // ("Menu distribution" / "REWARDS" for Coca-Cola Quick, which has no coupons),
  // so the pipeline names it rather than the UI branching per report. Same idea
  // as the per-metric `label`.
  title: z.string().optional(),
  tag: z.string().optional(),
  kpis: z.object({
    rewards_awarded: countSchema,
    // Redeemed, not just won. Optional — see engagement_kpis.rewards_claimed.
    rewards_claimed: countSchema.optional(),
    // Optional: computed by some markets but not rendered by any view. Spain
    // omits it (the dedicated DocDB count was ~96s for a metric nobody sees).
    won_not_claimed: countSchema.optional(),
    // Optional: omitted by campaigns with no coupon-inventory number (50/3) —
    // the view drops the tile instead of showing a meaningless 0.
    coupons_remaining: countSchema.optional(),
    contact_you_leads: countSchema,
    // New email / in-store usage splits. Optional — only regions that wire
    // up coupon redemption tracking ship these (BR first, others to follow).
    coupons_used_email: countSchema.optional(),
    coupons_used_in_store: countSchema.optional(),
  }),
  top_prizes: z.array(topPrizeSchema),
  // Per-brand coupon usage table. Optional — absent on regions whose
  // pipeline hasn't been updated yet; the view hides the panel cleanly.
  coupons_by_brand: z.array(couponByBrandSchema).optional(),
  // Coupons-per-day-per-brand stacked series. Optional — Spain ships it today.
  coupons_by_day: brandDailyStackSchema.optional(),
  // Per-brand remaining coupon inventory. Optional — Brazil ships it today.
  coupon_stock: z.array(couponStockSchema).optional(),
  // Rewards awarded per calendar day, campaign-wide (no brand split). Optional
  // — campaigns with sponsor brands ship `coupons_by_day` instead.
  rewards_by_day: dailySeriesSchema.optional(),
  // Same chart, several series (awarded vs claimed) in the shared multi-series
  // daily shape — `brands` carries the series, as it does for 50/3's funding
  // sources. Preferred over `rewards_by_day` when present. The series may
  // OVERLAP (a claimed reward was also awarded), so the view draws them side by
  // side rather than stacked.
  rewards_by_day_stack: brandDailyStackSchema.optional(),
});

// France-only consent breakdown. `by_type` is a sorted-desc array of
// {type, count} (label optional — UI falls back to a humanized `type`).
// `by_day` is a continuous date series across the campaign window — empty
// days carry value: 0 so the chart renders without gaps.
const consentByTypeSchema = z.object({
  type: z.string(),
  count: z.number(),
  label: z.string().optional(),
});

const consentsSectionSchema = z.object({
  by_type: z.array(consentByTypeSchema),
  by_day: dailySeriesSchema,
});

export const worldCupPayloadSchema = z.object({
  campaign: campaignSchema,
  sections: z.object({
    overview: overviewSectionSchema,
    engagement: engagementSectionSchema,
    games_missions: gamesMissionsSectionSchema,
    rewards_coupons: rewardsCouponsSectionSchema,
    // Optional — only France ships it today. Drives the Consents sub-tab.
    consents: consentsSectionSchema.optional(),
  }),
});

export const worldCupEnvelopeSchema = z.object({
  data: worldCupPayloadSchema,
  meta: z.object({
    report_id: z.string().optional(),
    title: z.string().optional(),
    shape: z.literal('document').optional(),
    last_refreshed_at: z.string().nullable(),
  }),
});

export type Money = z.infer<typeof moneySchema>;
export type Count = z.infer<typeof countSchema>;
export type Rate = z.infer<typeof rateSchema>;
export type WeeklySeries = z.infer<typeof weeklySeriesSchema>;
export type DailySeries = z.infer<typeof dailySeriesSchema>;
export type ConsentByType = z.infer<typeof consentByTypeSchema>;
export type ConsentsSection = z.infer<typeof consentsSectionSchema>;
export type Brand = z.infer<typeof brandSchema>;
export type Campaign = z.infer<typeof campaignSchema>;
export type OverviewSegment = z.infer<typeof overviewSegmentSchema>;
export type OverviewSection = z.infer<typeof overviewSectionSchema>;
export type EngagementSection = z.infer<typeof engagementSectionSchema>;
export type FunnelStep = z.infer<typeof funnelStepSchema>;
export type GamesMissionsSection = z.infer<typeof gamesMissionsSectionSchema>;
export type GamesByBrand = z.infer<typeof gamesByBrandSchema>;
export type RewardsCouponsSection = z.infer<typeof rewardsCouponsSectionSchema>;
export type TopPrize = z.infer<typeof topPrizeSchema>;
export type CouponByBrand = z.infer<typeof couponByBrandSchema>;
export type CouponStock = z.infer<typeof couponStockSchema>;
export type BrandDailyStack = z.infer<typeof brandDailyStackSchema>;
export type WorldCupPayload = z.infer<typeof worldCupPayloadSchema>;
export type WorldCupEnvelope = z.infer<typeof worldCupEnvelopeSchema>;
